import { SalesDetailsResponse } from '@/@types/Sells/get-details';
import { ISellersResponse } from '@/@types/Sells/get-sells';
import { PaymentMethodAvailable } from '@/@types/User';
import { formatCheckoutLabel } from '@/utils/checkoutTemplates';
import {
  addArrayFiltersToParams,
  addStringFiltersToParams,
  cleanFilterParams,
} from '@/utils/convertFilters';
import { api } from './api';

export interface SellsFilterParams {
  status?: string[];
  payment_methods?: string[];
  products?: string[];
  offers?: string[];
  coproducers?: string[];
  affiliates?: string[];
  templates?: string[];
  utm_campaign?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_content?: string;
  utm_term?: string;
  src?: string;
  currency?: string;
  invoice_approved?: boolean;
  invoice_not_approved?: boolean;
}

export interface GetSellsParams {
  page: number;
  startDate: string;
  endDate: string;
  dateAxis?: 'processing' | 'paid' | 'refunded';
  filterParams?: SellsFilterParams;
  q?: string;
  currency?: string;
}

export interface ExportSalesParams {
  startDate: string;
  endDate: string;
  dateAxis?: 'processing' | 'paid' | 'refunded';
  filterParams?: SellsFilterParams;
  q?: string;
}

export type PartnerRole = 'coproducer' | 'affiliate';

function appendDateAxisParams(
  urlParams: URLSearchParams,
  dateAxis:
    | GetSellsParams['dateAxis']
    | ExportSalesParams['dateAxis']
    | undefined,
  startDate: string,
  endDate: string,
) {
  if (dateAxis === 'paid') {
    urlParams.append('paid_start_date', startDate);
    urlParams.append('paid_end_date', endDate);
    return;
  }

  if (dateAxis === 'refunded') {
    urlParams.append('refunded_start_date', startDate);
    urlParams.append('refunded_end_date', endDate);
    return;
  }

  urlParams.append('start_date', startDate);
  urlParams.append('end_date', endDate);
}

async function getSells(params: GetSellsParams): Promise<ISellersResponse> {
  const { page, startDate, endDate, dateAxis, filterParams, q, currency } =
    params;
  const urlParams = new URLSearchParams();

  urlParams.append('page', page.toString());
  appendDateAxisParams(urlParams, dateAxis, startDate, endDate);
  urlParams.append('currency', currency);

  const cleanedFilterParams = cleanFilterParams(filterParams);
  addArrayFiltersToParams(urlParams, cleanedFilterParams);
  addStringFiltersToParams(urlParams, cleanedFilterParams);

  if (cleanedFilterParams?.invoice_approved === true) {
    urlParams.append('invoice_approved', '1');
  }
  if (cleanedFilterParams?.invoice_not_approved === true) {
    urlParams.append('invoice_not_approved', '1');
  }

  if (q && q.length > 1) {
    urlParams.append('q', q);
  }

  const response = await api.get<ISellersResponse>(
    `/v1/sells?${urlParams.toString()}`,
  );
  return response.data;
}

async function downloadExportSalesCSV(
  params: ExportSalesParams,
): Promise<void> {
  const { startDate, endDate, dateAxis, filterParams, q } = params;
  const urlParams = new URLSearchParams();

  appendDateAxisParams(urlParams, dateAxis, startDate, endDate);

  const cleanedFilterParams = cleanFilterParams(filterParams);
  addArrayFiltersToParams(urlParams, cleanedFilterParams);
  addStringFiltersToParams(urlParams, cleanedFilterParams);

  if (cleanedFilterParams?.invoice_approved === true) {
    urlParams.append('invoice_approved', '1');
  }
  if (cleanedFilterParams?.invoice_not_approved === true) {
    urlParams.append('invoice_not_approved', '1');
  }

  if (q && q.length > 1) {
    urlParams.append('q', q);
  }

  const response = await api.get(`/v1/exports?${urlParams.toString()}`, {
    responseType: 'blob',
  });

  createAndTriggerDownload(response.data, startDate, endDate);
}

async function getSellInfo(id: string): Promise<SalesDetailsResponse['data']> {
  const response = await api.get<SalesDetailsResponse>(`/v1/sells/${id}`);
  return response.data.data;
}

async function refund(orderId: string): Promise<any> {
  const response = await api.post(`/v1/refund/sanctum/${orderId}`);
  return response.data;
}

async function getPartnersByRole(
  role: PartnerRole,
  query?: string,
): Promise<Array<{ value: string; label: string }>> {
  const params = new URLSearchParams();
  params.append('role', role);
  if (query) params.append('q', query);

  const response = await api.get(
    `/v1/users/partners-by-role?${params.toString()}`,
  );

  return response.data.data.map((partner: any) => ({
    value: partner.id,
    label: `${partner.name} - ${partner.email}`,
  }));
}

function createAndTriggerDownload(
  data: Blob,
  startDate: string,
  endDate: string,
): void {
  const url = window.URL.createObjectURL(new Blob([data]));
  const link = document.createElement('a');

  link.href = url;
  link.setAttribute(
    'download',
    `mundpay-orders-export-${startDate}_to_${endDate}.csv`,
  );
  document.body.appendChild(link);
  link.click();
  link.remove();
}

interface StartExportResponse {
  message: string;
  report_id: number;
  status: string;
}

interface ExportStatusResponse {
  id: number;
  type: string;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  download_url?: string;
  expires_at?: string;
  error_message?: string;
}

async function startExportSales(
  params: ExportSalesParams,
): Promise<StartExportResponse> {
  const { startDate, endDate, dateAxis, filterParams, q } = params;
  const urlParams = new URLSearchParams();

  appendDateAxisParams(urlParams, dateAxis, startDate, endDate);

  const cleanedFilterParams = cleanFilterParams(filterParams);
  addArrayFiltersToParams(urlParams, cleanedFilterParams);
  addStringFiltersToParams(urlParams, cleanedFilterParams);

  if (cleanedFilterParams?.invoice_approved === true) {
    urlParams.append('invoice_approved', '1');
  }
  if (cleanedFilterParams?.invoice_not_approved === true) {
    urlParams.append('invoice_not_approved', '1');
  }

  if (q && q.length > 1) {
    urlParams.append('q', q);
  }

  const response = await api.post<StartExportResponse>(
    `/v1/exports/orders?${urlParams.toString()}`,
  );
  return response.data;
}

async function checkExportStatus(
  reportId: number,
): Promise<ExportStatusResponse> {
  const response = await api.get<ExportStatusResponse>(
    `/v1/exports/${reportId}`,
  );

  return response.data;
}

async function cancelExport(
  reportId: number,
  errorMessage?: string,
): Promise<{ message: string }> {
  const config: any = {};
  if (errorMessage) {
    config.data = {
      error_message: errorMessage,
    };
  }
  const response = await api.delete<{ message: string }>(
    `/v1/exports/${reportId}`,
    config,
  );
  return response.data;
}

export interface NotazzAvailabilityResponse {
  available: boolean;
  reason?: string;
  message?: string;
  issues?: Array<{
    field: string;
    message: string;
    missing_fields?: string[];
  }>;
  integration_id?: string;
  integration_name?: string;
}

/** Resposta do endpoint "emitir ou consultar" (POST notazz-nfse/sync — síncrono) */
export interface NotazzNfseEnsureResponse {
  success: boolean;
  message: string;
  action?: 'emitted' | 'consulted';
  data?: {
    statusNota: string | null;
    pdf: string | null;
    xml: string | null;
    numero: string | null;
    codigoVerificacao: string | null;
    linkPrefeitura: string | null;
  };
  error?: {
    code: string;
    notazz_code?: string;
    notazz_message?: string;
    issues?: Array<{
      field: string;
      message: string;
      missing_fields?: string[];
    }>;
  };
}

async function checkNotazzAvailability(
  orderUuid: string,
): Promise<NotazzAvailabilityResponse> {
  const response = await api.get<NotazzAvailabilityResponse>(
    `/v1/sells/${orderUuid}/notazz-availability`,
  );
  return response.data;
}

/** Timeout longo: chamada síncrona ao backend + API Notazz na mesma requisição. */
const NOTAZZ_EMIT_SYNC_TIMEOUT_MS = 180_000;

/**
 * Emite a nota se ainda não foi processada, ou consulta o status (fluxo 100% síncrono no servidor).
 * Usa POST dedicado para evitar cache de GET e permitir timeout explícito no cliente.
 */
async function getNotazzNfseEnsureAndStatus(
  orderUuid: string,
): Promise<NotazzNfseEnsureResponse> {
  const response = await api.post<NotazzNfseEnsureResponse>(
    `/v1/sells/${orderUuid}/notazz-nfse/sync`,
    {},
    { timeout: NOTAZZ_EMIT_SYNC_TIMEOUT_MS },
  );
  return response.data;
}

/** NF-e de devolução Notazz (DOCUMENT_GOAL 4) — mesmo timeout que emissão síncrona. */
async function postNotazzNfeReturnSync(
  orderUuid: string,
  body: { cfop: string },
): Promise<NotazzNfseEnsureResponse> {
  const response = await api.post<NotazzNfseEnsureResponse>(
    `/v1/sells/${orderUuid}/notazz-nfe-return/sync`,
    body,
    { timeout: NOTAZZ_EMIT_SYNC_TIMEOUT_MS },
  );
  return response.data;
}

/** NF-e de devolução eNotas — mesmo timeout que emissão síncrona Notazz. */
async function postEnotasNfeReturnSync(
  orderUuid: string,
  body: { cfop: string },
): Promise<NotazzNfseEnsureResponse> {
  const response = await api.post<NotazzNfseEnsureResponse>(
    `/v1/sells/${orderUuid}/enotas-nfe-return/sync`,
    body,
    { timeout: NOTAZZ_EMIT_SYNC_TIMEOUT_MS },
  );
  return response.data;
}

export interface EnotasAvailabilityResponse {
  available: boolean;
  reason?: string;
  message?: string;
  issues?: Array<{
    field: string;
    message: string;
    missing_fields?: string[];
  }>;
  integration_id?: string;
  integration_name?: string;
}

/** Resposta do endpoint "emitir ou consultar" (GET enotas-nfse) */
export interface EnotasNfseEnsureResponse {
  success: boolean;
  message: string;
  action?: 'emitted' | 'consulted';
  data?: {
    statusNota: string | null;
    pdf: string | null;
    xml: string | null;
    numero: string | null;
    codigoVerificacao: string | null;
  };
  error?: {
    code: string;
    enotas_message?: string;
  };
}

async function checkEnotasAvailability(
  orderUuid: string,
): Promise<EnotasAvailabilityResponse> {
  const response = await api.get<EnotasAvailabilityResponse>(
    `/v1/sells/${orderUuid}/enotas-availability`,
  );
  return response.data;
}

async function getEnotasNfseEnsureAndStatus(
  orderUuid: string,
): Promise<EnotasNfseEnsureResponse> {
  const response = await api.get<EnotasNfseEnsureResponse>(
    `/v1/sells/${orderUuid}/enotas-nfse`,
  );
  return response.data;
}

async function getSoldProducts(
  query?: string,
  currency: string = 'BRL',
): Promise<Array<{ value: string; label: string }>> {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  params.append('currency', currency);

  const response = await api.get(`/v1/sells/sold-products?${params.toString()}`);
  return response.data.data;
}

async function getSoldOffers({
  productIds,
  query,
  currency = 'BRL',
}: {
  productIds?: string[];
  query?: string;
  currency?: string;
}): Promise<Array<{ value: string; label: string }>> {
  const params = new URLSearchParams();
  params.append('currency', currency);
  if (query) params.append('q', query);
  if (productIds?.length) {
    params.append('products', productIds.join(','));
  }

  const response = await api.get(`/v1/sells/sold-offers?${params.toString()}`);
  return response.data.data;
}

async function getSoldCheckouts({
  productIds,
  offerIds,
  query,
  currency = 'BRL',
}: {
  productIds?: string[];
  offerIds?: string[];
  query?: string;
  currency?: string;
}): Promise<Array<{ value: string; label: string }>> {
  const params = new URLSearchParams();
  params.append('currency', currency);
  if (query) params.append('q', query);
  if (productIds?.length) {
    params.append('products', productIds.join(','));
  }
  if (offerIds?.length) {
    params.append('offers', offerIds.join(','));
  }

  const response = await api.get(`/v1/sells/sold-checkouts?${params.toString()}`);
  const checkouts = response.data.data as Array<{
    value: string;
    label: string;
    template?: string | null;
  }>;

  return checkouts.map(({ value, label, template }) => ({
    value,
    label: formatCheckoutLabel(label, template),
  }));
}

async function getSoldAffiliates({
  productIds,
  query,
}: {
  productIds?: string[];
  query?: string;
}): Promise<Array<{ value: string; label: string }>> {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  if (productIds?.length) {
    params.append('products', productIds.join(','));
  }

  const response = await api.get(`/v1/sells/sold-affiliates?${params.toString()}`);
  return response.data.data;
}

async function getSoldCoproducers({
  productIds,
  query,
}: {
  productIds?: string[];
  query?: string;
}): Promise<Array<{ value: string; label: string }>> {
  const params = new URLSearchParams();
  if (query) params.append('q', query);
  if (productIds?.length) {
    params.append('products', productIds.join(','));
  }

  const response = await api.get(`/v1/sells/sold-coproducers?${params.toString()}`);
  return response.data.data;
}

async function getSoldPaymentMethods(
  currency: string = 'BRL',
): Promise<PaymentMethodAvailable[]> {
  const params = new URLSearchParams({ currency });
  const response = await api.get<{ data: PaymentMethodAvailable[] }>(
    `/v1/sells/sold-payment-methods?${params.toString()}`,
  );
  return response.data.data;
}

export const SellsService = {
  getSells,
  getSellInfo,
  getPartnersByRole,
  downloadExportSalesCSV,
  startExportSales,
  checkExportStatus,
  cancelExport,
  refund,
  checkNotazzAvailability,
  getNotazzNfseEnsureAndStatus,
  postNotazzNfeReturnSync,
  postEnotasNfeReturnSync,
  checkEnotasAvailability,
  getEnotasNfseEnsureAndStatus,
  getSoldProducts,
  getSoldOffers,
  getSoldCheckouts,
  getSoldAffiliates,
  getSoldCoproducers,
  getSoldPaymentMethods,
};
